import { Spinner } from '@frontend/components/common';
import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { Package } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import type React from 'react';
import { LuCheck, LuInfo, LuMinus, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import KioskList from '../../../components/kiosk-list.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import { ReturnEquipmentChoice, useReturnEquipmentList } from './return-equipment-list.controller';

const ID = 'return-equipment-list';

const choiceLabels: Record<ReturnEquipmentChoice, React.ReactNode> = {
    normal: (
        <FormattedMessage
            id='kiosk.wizard.return-equipment-list.return-normal'
            defaultMessage='Return'
            description='Button label for returning selected equipment in normal condition'
        />
    ),
    lost: (
        <FormattedMessage
            id='kiosk.wizard.return-equipment-list.lost'
            defaultMessage='Lost'
            description='Button label to mark selected return equipment as lost'
        />
    ),
    damaged: (
        <FormattedMessage
            id='kiosk.wizard.return-equipment-list.damaged'
            defaultMessage='Damaged'
            description='Button label to mark selected return equipment as damaged'
        />
    ),
    tag_broken: (
        <FormattedMessage
            id='kiosk.wizard.return-equipment-list.tag-broken'
            defaultMessage='Tag broken'
            description='Button label to mark selected return equipment as having a broken tag'
        />
    ),
    incomplete: (
        <FormattedMessage
            id='kiosk.wizard.return-equipment-list.incomplete'
            defaultMessage='Incomplete'
            description='Button label to mark selected return equipment as incomplete'
        />
    )
};

const requiresVerification = (choice: ReturnEquipmentChoice) => choice === 'normal' || choice === 'damaged';
const getVisibleChoices = (equipment: IEquipment | null, canMarkIncomplete: boolean): ReturnEquipmentChoice[] => {
    const choices: ReturnEquipmentChoice[] = equipment?.state === EquipmentStateName.EQUIPMENT_LOST ? ['normal', 'damaged'] : ['normal', 'lost', 'damaged'];
    if (canMarkIncomplete) choices.push('incomplete');
    return choices;
};

const VerificationHelpCard = (props: {
    choice: ReturnEquipmentChoice;
    isVerified: boolean;
    verificationRequired: boolean;
    allowUnidentifiedEquipmentReturn: boolean;
    onTagBroken: () => void;
}) => {
    const needsVerificationForChoice = requiresVerification(props.choice);

    if (props.verificationRequired && !props.isVerified) {
        return (
            <div className='bg-background-surface inline-flex min-h-24 items-center justify-between gap-4 self-stretch rounded-xl p-4'>
                <div className='inline-flex items-center justify-start gap-3'>
                    <LuMinus className='bg-status-warning text-background-surface size-5 shrink-0 rounded-full p-1' />
                    <div className='text-foreground-default justify-start text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.return-equipment-list.scan-to-verify'
                            defaultMessage='Scan this equipment to verify it before continuing.'
                            description='Help text shown when selected return equipment must be scanned before continuing'
                        />
                    </div>
                </div>
                {props.choice === 'damaged' && (
                    <button
                        type='button'
                        onClick={props.onTagBroken}
                        className='border-brand-primary text-brand-primary bg-background-surface inline-flex h-11 shrink-0 items-center justify-center rounded-xl border px-4 text-base font-medium'>
                        {choiceLabels.tag_broken}
                    </button>
                )}
            </div>
        );
    }

    if (needsVerificationForChoice && props.allowUnidentifiedEquipmentReturn && !props.isVerified) {
        return (
            <div className='bg-background-surface inline-flex min-h-24 items-center justify-start gap-3 self-stretch rounded-xl p-4'>
                <LuInfo className='text-brand-primary size-5 shrink-0' />
                <div className='text-foreground-default justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.return-equipment-list.unidentified-return-allowed'
                        defaultMessage='No scan is needed for this return.'
                        description='Help text shown when return equipment verification is disabled by settings'
                    />
                </div>
            </div>
        );
    }

    if (needsVerificationForChoice) {
        return (
            <div className='bg-background-surface inline-flex min-h-24 items-center justify-start gap-3 self-stretch rounded-xl p-4'>
                <LuCheck className='bg-status-success text-background-surface size-5 shrink-0 rounded-full p-1' />
                <div className='text-foreground-default justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.return-equipment-list.equipment-verified'
                        defaultMessage='Equipment verified.'
                        description='Help text shown when selected return equipment has been scanned and verified'
                    />
                </div>
            </div>
        );
    }

    if (props.choice === 'lost') {
        return (
            <div className='bg-background-surface inline-flex min-h-24 items-center justify-start gap-3 self-stretch rounded-xl p-4'>
                <LuInfo className='text-brand-primary size-5 shrink-0' />
                <div className='text-foreground-default justify-start text-base font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.return-equipment-list.lost-no-scan-needed'
                        defaultMessage='No scan is needed because this equipment is marked as lost.'
                        description='Help text shown when selected return equipment is marked lost and does not require scanning'
                    />
                </div>
            </div>
        );
    }

    return (
        <div className='bg-background-surface inline-flex min-h-24 items-center justify-start gap-3 self-stretch rounded-xl p-4'>
            <LuInfo className='text-brand-primary size-5 shrink-0' />
            <div className='text-foreground-default justify-start text-base font-medium'>
                <FormattedMessage
                    id='kiosk.wizard.return-equipment-list.no-scan-needed'
                    defaultMessage='No scan is needed for this return condition.'
                    description='Help text shown when the selected return condition does not require scanning'
                />
            </div>
        </div>
    );
};

export const ReturnEquipmentList = () => {
    const viewProps = useReturnEquipmentList();

    const canSubmit = !!viewProps.selectedEquipment && (viewProps.isVerified || !viewProps.verificationRequired) && !viewProps.configError && !viewProps.isBusy;

    return (
        <WizardScreenContainer
            id={ID}
            footer={
                viewProps.isDetailOpen
                    ? {
                          onBack: viewProps.onClearSelection,
                          onSubmit: viewProps.onSubmit,
                          submitDisabled: !canSubmit,
                          submitLabel: (
                              <FormattedMessage
                                  id='kiosk.wizard.return-equipment-list.confirm'
                                  defaultMessage='Confirm return'
                                  description='Footer button label to confirm the selected equipment return condition'
                              />
                          )
                      }
                    : {
                          onBack: viewProps.onBack,
                          onSubmit: viewProps.onContinueSelection,
                          submitDisabled: !viewProps.selectedEquipment,
                          submitLabel: CommonMessage.BUTTONS.CONTINUE
                      }
            }
            loadingModal={{ show: viewProps.isSubmitting || viewProps.isWaitingForEquipmentState }}
            errorModal={{
                show: !!viewProps.error,
                title: (
                    <FormattedMessage
                        id='kiosk.wizard.return-equipment-list.error-title'
                        defaultMessage='Return error'
                        description='Title shown in the error modal on the return equipment list screen'
                    />
                ),
                message: viewProps.error,
                onClose: viewProps.resetError
            }}
            containerClassName='flex size-full flex-col overflow-x-hidden overflow-y-auto p-6 gap-3'>
            {viewProps.isDetailOpen ? <SelectedEquipmentDetail {...viewProps} /> : <EquipmentSelectionList {...viewProps} />}
        </WizardScreenContainer>
    );
};

const EquipmentSelectionList = (props: ReturnType<typeof useReturnEquipmentList>) => (
    <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
        <div className='text-foreground-default justify-end'>
            <FormattedMessage
                id='kiosk.wizard.return-equipment-list.scan-or-select'
                defaultMessage='Scan your equipment or select it from the list.'
                description='Instruction text on the return equipment list screen, prompting users to scan or select equipment to return'
            />
        </div>

        {props.configError && <ConfigError />}

        <KioskList<IEquipment>
            id='return-equipment-list'
            fetch={props.fetchEquipments}
            rowElement={(equipment) => (
                <ProductCard
                    key={equipment.id}
                    product_id={equipment.product_id}
                    equipment={equipment}
                    onClick={() => props.onSelectEquipment(equipment)}
                />
            )}
            emptyText={
                <FormattedMessage
                    id='kiosk.wizard.return-equipment-list.no-equipment'
                    defaultMessage='No equipment to return'
                    description='Empty state text shown when the current user has no equipment available to return'
                />
            }
        />
    </div>
);

const SelectedEquipmentDetail = (props: ReturnType<typeof useReturnEquipmentList>) => {
    const verificationComplete = props.isVerified || !props.verificationRequired;

    return (
        <div className='flex flex-1 flex-col items-start justify-start gap-4 self-stretch pb-20'>
            <div className='text-foreground-default justify-end'>
                <FormattedMessage
                    id='kiosk.wizard.return-equipment-list.detail-instructions'
                    defaultMessage='Choose how you want to return this equipment.'
                    description='Instruction text on the selected equipment detail state of the return equipment list screen'
                />
            </div>

            {props.configError && <ConfigError />}

            {props.isLoadingSelection ? (
                <div className='inline-flex h-full w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                    <Spinner />
                </div>
            ) : (
                <>
                    <div className='bg-background-surface inline-flex w-full flex-col gap-4 rounded-xl p-4'>
                        <div className='inline-flex items-center justify-start gap-4 self-stretch'>
                            <div className='outline-border-default relative flex size-12 shrink-0 items-center justify-center rounded-full outline-2'>
                                <Package className='size-1/2' />
                                {verificationComplete ? (
                                    <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                ) : (
                                    <LuMinus className='bg-status-warning text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                )}
                            </div>
                            <div className='inline-flex min-w-0 flex-1 flex-col items-start justify-center gap-1'>
                                <div className='text-foreground-default line-clamp-2 justify-start text-base font-semibold'>
                                    {props.selectedProduct?.name ?? (
                                        <FormattedMessage
                                            id='kiosk.wizard.return-equipment-list.unknown-product'
                                            defaultMessage='Unknown product'
                                            description='Fallback product name shown when the selected equipment product cannot be loaded'
                                        />
                                    )}
                                </div>
                                <div className='text-brand-muted justify-start text-base font-medium'>{props.selectedEquipment?.serial_number}</div>
                            </div>
                        </div>
                    </div>

                    <div className='flex w-full flex-col gap-3'>
                        <div className='text-foreground-default justify-start text-base font-medium'>
                            <FormattedMessage
                                id='kiosk.wizard.return-equipment-list.condition'
                                defaultMessage='Condition'
                                description='Label above the return condition selector for selected equipment'
                            />
                        </div>
                        <div className='grid w-full grid-cols-4 gap-2'>
                            {getVisibleChoices(props.selectedEquipment, props.canMarkIncomplete).map((choice) => (
                                <button
                                    key={choice}
                                    type='button'
                                    onClick={() => props.setSelectedChoice(choice)}
                                    className={`inline-flex h-14 items-center justify-center rounded-xl border px-3 text-base font-medium ${
                                        props.selectedChoice === choice || (choice === 'damaged' && props.selectedChoice === 'tag_broken')
                                            ? 'border-brand-primary bg-brand-primary text-background-surface'
                                            : 'border-brand-primary text-brand-primary bg-background-surface'
                                    }`}>
                                    {choiceLabels[choice]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <VerificationHelpCard
                        choice={props.selectedChoice}
                        isVerified={props.isVerified}
                        verificationRequired={props.verificationRequired}
                        allowUnidentifiedEquipmentReturn={props.allowUnidentifiedEquipmentReturn}
                        onTagBroken={() => props.setSelectedChoice('tag_broken')}
                    />
                </>
            )}
        </div>
    );
};

const ConfigError = () => (
    <div className='bg-background-surface inline-flex items-center justify-start self-stretch rounded-xl p-4'>
        <div className='text-status-danger justify-start text-base font-medium'>
            <FormattedMessage
                id='kiosk.wizard.return-equipment-list.config-error'
                defaultMessage='This distriSPOT is not configured for returns. Please contact support.'
                description='Configuration error shown when the current distriSPOT cannot process equipment returns'
            />
        </div>
    </div>
);

export default ReturnEquipmentList;
